import { collection } from 'infra/collection'
import Member from './models/Member'
import DataLoader from 'dataloader'
import { IAMAPI } from './API'
import {
  GetManyMembersPayload,
  GetMemberPayload,
  FindMembersQuery,
  CreateMemberPayload,
  MemberDTO,
  MemberMetadataDTO,
  GetMemberByPublicIdPayload,
  GetManyMembersByPublicIdPayload,
  UpdateMemberPayload,
  CreateMemberManuallyPayload,
} from './dtos/member'
import { PrimeUser } from './UserStore'
import { PrimeWorkspace } from './WorkspaceStore'
import { Id, PrimeProps, CursorPaged } from 'types/definitions'
import { PrimeRole } from './RoleStore'
import { PrimeSkill } from './SkillStore'
import Skill from './models/Skill'
import Role from './models/Role'
import { CancellationToken } from '../infra/cancellation'
import Workspace from './models/Workspace'
import { MagicLinkDestination } from './dtos/magic-link'

/**
 * Used for the `primeMember` function.
 */
export type PrimeMember = (
  ids: PrimeProps<MemberDTO, 'workspace_id' | 'user_id' | 'user_public_id'>,
) => Member

/**
 * Used for the `primeFullMember` function.
 */
export type PrimeFullMember = (memberDto: MemberDTO) => Member

/**
 * Used for the `primeMemberByPublicId` function.
 */
export type PrimeMemberByPublicId = (
  ids: PrimeProps<MemberDTO, 'workspace_id' | 'user_public_id'>,
) => Member

/**
 * Gets a member by workspace + user ID.
 */
export type GetMember = (workspaceId: Id, userId: Id) => Member | undefined

/**
 * Gets a member by workspace id and user public id.
 */
export type GetMemberByPublicId = (
  workspaceId: Id,
  userPublicId: Id,
) => Member | undefined

/**
 * Callback for finding members.
 */
export type FindMembers = (
  params: FindMembersQuery,
  cancellationToken?: CancellationToken,
) => Promise<CursorPaged<Member>>

/**
 * Get a list of members using a list of user public ids.
 */
export type GetManyMembersByPublicId = (
  params: GetManyMembersByPublicIdPayload,
) => Promise<Array<Member | null>>

/**
 * Manages members, which are users that belong to a workspace.
 */
export default class MemberStore {
  /**
   * Cache for the member loader.
   * @private
   */
  private readonly memberLoaderByUserPublicIdCache = new Map()

  /**
   * Used to compute a cache key from params.
   */
  private cacheKeyFn = (params: Partial<{ user_id: Id; workspace_id: Id }>) =>
    getCompositeMemberId(params.workspace_id, params.user_id)

  /**
   * Used to compute a cache key from workspace id and user public id.
   */
  private cacheKeyFnByUserPublicId = (
    params: Partial<{ user_public_id: Id; workspace_id: Id }>,
  ) => getCompositeMemberId(params.workspace_id, params.user_public_id)

  /**
   * Members collection.
   */
  members = collection<MemberDTO, Member>({
    // We don't use the Member `id` for anything, always workspace + user ID.
    getDataId: (data) =>
      getCompositeMemberId(data.workspace_id, data.user_public_id),
    getModelId: (model) =>
      getCompositeMemberId(model.workspace.id, model.user.publicId),
    create: () =>
      new Member(
        this.primeWorkspace,
        this.primeUser,
        this.primeRole,
        this.primeSkill,
      ),
  })

  constructor(
    private api: IAMAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeRole: PrimeRole,
    private primeSkill: PrimeSkill,
  ) {
    this.updateLastLogin = this.updateLastLogin.bind(this)
    this.updateLastSeen = this.updateLastSeen.bind(this)
  }

  /**
   * Data loader.
   */
  memberLoader = new DataLoader(this._fetchMany.bind(this), {
    cacheKeyFn: this.cacheKeyFn,
  })

  /**
   * Data loader using public ID.
   */
  memberLoaderByUserPublicId = new DataLoader(
    this._fetchManyByPublicId.bind(this),
    {
      cacheKeyFn: this.cacheKeyFnByUserPublicId,
      cache: this.memberLoaderByUserPublicIdCache as any,
    },
  )

  /**
   * Returns a member from the collection matching the workspace and user IDs.
   */
  getMember: GetMember = (workspaceId: Id, userId: Id): Member | undefined => {
    if (!workspaceId || !userId) {
      return undefined
    }
    return this.members.find(
      (x) => x.workspace.id === workspaceId && x.user.id === userId,
    )
  }

  /**
   * Returns a member from the collection matching the workspace and user public IDs.
   */
  getMemberByUserPublicId: GetMemberByPublicId = (
    workspaceId: Id,
    userPublicId: Id,
  ): Member | undefined => {
    if (!workspaceId || !userPublicId) {
      return undefined
    }
    return this.members.find(
      (x) => x.workspace.id === workspaceId && x.user.publicId === userPublicId,
    )
  }

  /**
   * Sets and fetches the member if not been fetched before using the user public ID.
   */
  primeMemberByPublicId: PrimeMemberByPublicId = (props) => {
    if (!props) {
      return Member.none
    }
    if (!Id.some(props.workspace_id)) {
      return Member.none
    }

    if (!Id.some(props.user_public_id)) {
      return Member.none
    }

    this.fetchByUserPublicId({
      workspace_id: props.workspace_id,
      user_public_id: props.user_public_id,
    })

    const member = this.members.set({
      workspace_id: props.workspace_id,
      user_public_id: props.user_public_id,
    })

    // NOTE: We used to check for the non-default of dateCreated
    // but the id-query endpoint no longer returns it.
    if (member.internalId) {
      // The member has already been hydrated from the server, so don't
      // assign any props to it
      return member
    }

    return member.set(props, {
      parse: true,
      stripUndefined: true,
    })
  }

  /**
   * Primes the member cache based on the specified DTO.
   *
   * @param memberDto
   */
  primeFullMember: PrimeFullMember = (memberDto) => {
    const key = {
      user_public_id: memberDto.user_public_id,
      workspace_id: memberDto.workspace_id,
    }
    const cacheKey = this.cacheKeyFnByUserPublicId(key)

    if (this.memberLoaderByUserPublicIdCache.has(cacheKey)) {
      return this.members.set({
        workspace_id: memberDto.workspace_id,
        user_public_id: memberDto.user_public_id,
      })
    }

    const member = this.members.set(memberDto)
    this.memberLoaderByUserPublicId.prime(key, member)
    return member
  }

  /**
   * Sets and fetches the member if not been fetched before.
   */
  primeMember: PrimeMember = (props) => {
    if (!props) {
      return Member.none
    }
    if (!Id.some(props.workspace_id)) {
      // console.warn(
      //   new Error(
      //     'MemberStore.primeMember: props.workspace_id is required. Returning undefined.'
      //   )
      // )
      return Member.none
    }
    if (!Id.some(props.user_id)) {
      // console.warn(
      //   new Error(
      //     'MemberStore.primeMember: props.user_id is required. Returning undefined.'
      //   )
      // )
      return Member.none
    }

    this.fetch({
      workspace_id: props.workspace_id,
      user_id: props.user_id,
      user_public_id: props.user_public_id,
    })

    const member = this.members.set({
      workspace_id: props.workspace_id,
      user_id: props.user_id,
      user_public_id: props.user_public_id,
    })

    // NOTE: We used to check for the non-default of dateCreated
    // but the id-query endpoint no longer returns it.
    if (member.internalId) {
      // The member has already been hydrated from the server, so don't
      // assign any props to it
      return member
    }

    return member.set(props, {
      parse: true,
      stripUndefined: true,
    })
  }

  forWorkspace(workspaceId: Id) {
    return this.members.filter((member) => member.workspace.id === workspaceId)
  }

  /**
   * Fetch a single member from cache DataLoader or API
   */
  async fetch(params: GetMemberPayload): Promise<Member | null> {
    if (!params.workspace_id || (!params.user_id && !params.user_public_id)) {
      console.warn(new Error('No workspace or user defined.'))
    }
    return params.user_public_id
      ? this.memberLoaderByUserPublicId.load({
          workspace_id: params.workspace_id,
          user_public_id: params.user_public_id,
        })
      : this.memberLoader.load(params)
  }

  /**
   * Fetch a single member from cache DataLoaderByUserPublicId or API
   */
  fetchByUserPublicId(
    params: GetMemberByPublicIdPayload,
  ): Promise<Member | null> {
    if (!params.workspace_id || !params.user_public_id) {
      console.warn(new Error('No workspace or user defined.'))
    }
    return this.memberLoaderByUserPublicId.load(params)
  }

  /**
   * Fetch multiple members from cache or API
   */
  async fetchMany(ids: GetManyMembersPayload): Promise<Array<Member | null>> {
    if (ids.some((x) => !x.workspace_id || (!x.user_id && !x.user_public_id))) {
      console.warn(new Error('No workspace or user defined'))
    }
    return Promise.all(ids.map((id) => this.fetch(id)))
  }

  /**
   * Search for members.
   * @param {*} params
   * @param cancellationToken
   */
  search: FindMembers = async (
    params: FindMembersQuery,
    cancellationToken = CancellationToken.default,
  ) => {
    return await this.api.members
      .find(
        params.workspace_id,
        {
          ...params,
        },
        cancellationToken,
      )
      .then((result) => ({
        ...result,
        data: this.members.set(result.data),
      }))
  }

  getManyByPublicIds: GetManyMembersByPublicId = async (
    ids: GetManyMembersByPublicIdPayload,
  ) => {
    if (ids.some((x) => !x.workspace_id || !x.user_public_id)) {
      console.warn(new Error('No workspace or user defined'))
    }
    return Promise.all(
      ids.map((id) => this.memberLoaderByUserPublicId.load(id)),
    )
  }

  /**
   * Adds a role to a member.
   */
  async addRole(workspaceId: string, userPublicId: string, roleId: string) {
    return this.api.members
      .addRole(
        { workspace_id: workspaceId, user_public_id: userPublicId },
        roleId,
      )
      .then(this.members.set)
  }

  /**
   * Adds a skill to a member.
   */
  async addSkill(member: Member, skill: Skill) {
    return this.api.members
      .addSkill(memberByPublicIdToParams(member), Id.assert(skill.id))
      .then(this.members.set)
  }

  /**
   * Adds many skills to a member.
   */
  async addManySkillsByPublicId(
    workspace: Workspace,
    userPublicId: Id,
    skills: Skill[],
  ) {
    return this.api.members
      .addManySkillsByPublicId({
        workspace_id: Id.assert(workspace.id),
        user_public_id: Id.assert(userPublicId),
        skill_ids: skills.map((skill) => Id.assert(skill.id)),
      })
      .then(this.members.set)
  }

  /**
   * Removes a role from a member.
   */
  async removeRole(member: Member, role: Role) {
    return this.api.members
      .removeRole(memberByPublicIdToParams(member), Id.assert(role.id))
      .then(this.members.set)
  }

  /**
   * Removes a skill from a member.
   */
  async removeSkill(member: Member, skill: Skill) {
    return this.api.members
      .removeSkill(memberByPublicIdToParams(member), Id.assert(skill.id))
      .then(this.members.set)
  }

  /**
   * Removes many skills from a member.
   */
  async removeManySkillsByPublicId(
    workspace: Workspace,
    userPublicId: Id,
    skills: Skill[],
  ) {
    return this.api.members
      .removeManySkillsByPublicId({
        workspace_id: Id.assert(workspace.id),
        user_public_id: Id.assert(userPublicId),
        skill_ids: skills.map((skill) => Id.assert(skill.id)),
      })
      .then(this.members.set)
  }

  /**
   * Creates a workspace member.
   */
  create(data: CreateMemberPayload): Promise<Member> {
    return this.api.members.create(data).then((data) => this.members.set(data))
  }

  /**
   * Used for manually creating another member.
   */
  createManually(data: CreateMemberManuallyPayload): Promise<Member> {
    return this.api.members
      .createManually(data)
      .then((data) => this.members.set(data))
  }

  /**
   * Updates a member.
   */
  async update(
    params: GetMemberByPublicIdPayload,
    data: UpdateMemberPayload,
  ): Promise<Member> {
    return this.api.members
      .update(params, data)
      .then((data) => this.members.set(data))
  }

  /**
   * Updates a member.
   */
  async updateProfile(
    member: Member,
    params: UpdateProfileParams,
  ): Promise<Member> {
    return this.api.members
      .update(memberByPublicIdToParams(member), {
        given_name: params.givenName,
        family_name: params.familyName,
        email: params.email,
        phone: params.phone,
        member_metadata: params.memberMetadata,
      })
      .then((data) => this.members.set(data))
  }

  /**
   * Updates a member's last_login date.
   */
  async updateLastLogin(member: Member) {
    return this.api.members.updateLastLogin(Id.assert(member.workspace.id))
  }

  /**
   * Updates a member's last_seen date.
   */
  async updateLastSeen(member: Member) {
    return this.api.members.updateLastSeen(Id.assert(member.workspace.id))
  }

  /**
   * Changes the avatar of the specified member.
   *
   * @param member
   * @param file
   */
  async changeAvatar(member: Member, file: File) {
    return this.api.members
      .changeAvatar(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        file,
      )
      .then(this.members.set)
  }

  async impersonate(
    userPublicId: string,
    workspaceId: string,
    callback: string,
  ) {
    return this.api.impersonation.impersonate(
      userPublicId,
      workspaceId,
      callback,
    )
  }

  /**
   * Generates a Magic Link for a given user.
   */
  async generateMagicLink(
    userPublicId: string,
    workspaceId: string,
    destination: MagicLinkDestination,
  ) {
    return this.api.magicLink.generate({
      user_public_id: userPublicId,
      workspace_id: workspaceId,
      destination,
    })
  }

  /**
   * Checks if the terms of use has been accepted.
   *
   * @param workspaceId
   * @returns
   */
  async checkTermsOfUseAcceptance(workspaceId: string) {
    return (
      workspaceId
        ? this.api.termsOfUse.checkAcceptanceForWorkspace(workspaceId)
        : this.api.termsOfUse.checkAcceptanceForPlatform()
    ).then((acceptance) => acceptance.accepted)
  }

  /**
   * Used by the DataLoader to fetch any number of members
   */
  async _fetchMany(ids: GetManyMembersPayload) {
    if (ids.length === 0) {
      return []
    }

    const membersToFetch = new Map()
    ids.forEach((params) => {
      const member = this.members.set(params)
      membersToFetch.set(params.workspace_id + params.user_id, member)
    })

    const members = await this.api.members.getMany(ids)
    members.forEach((item, index) => {
      if (!item) {
        // If the user is not found now, does not mean it won't be found later.
        this.memberLoader.clear(ids[index])
        this.members.remove(ids[index].user_id)
      } else {
        const target = membersToFetch.get(item.workspace_id + item.user_id)
        if (target) {
          target.set(item, {
            parse: true,
            stripUndefined: true,
          })
        }
      }
    })
    return members.map(
      (r) => (r && this.getMember(r.workspace_id, r.user_id)) || null,
    )
  }

  async _fetchManyByPublicId(ids: GetManyMembersByPublicIdPayload) {
    if (ids.length === 0) {
      return []
    }

    const membersToFetch = new Map()
    ids.forEach((params) => {
      const member = this.members.set(params)
      membersToFetch.set(params.workspace_id + params.user_public_id, member)
    })

    const members = await this.api.members.getManyByPublicId(ids)
    members.forEach((item, index) => {
      if (!item) {
        // If the user is not found now, does not mean it won't be found later.
        this.memberLoaderByUserPublicId.clear(ids[index])
        this.members.remove(ids[index].user_public_id)
      } else {
        const target = membersToFetch.get(
          item.workspace_id + item.user_public_id,
        )
        if (target) {
          target.set(item, {
            parse: true,
            stripUndefined: true,
          })
        }
      }
    })
    return members.map(
      (r) =>
        (r && this.getMemberByUserPublicId(r.workspace_id, r.user_public_id)) ||
        null,
    )
  }
}

function getCompositeMemberId(workspaceId?: Id, userId?: Id) {
  return workspaceId && userId ? `${workspaceId}/${userId}` : undefined
}

// TODO: Rename to original once all usages have been migrated
function memberByPublicIdToParams(member: Member): GetMemberByPublicIdPayload {
  return {
    workspace_id: Id.assert(member.workspace.id),
    user_public_id: Id.assert(member.user.publicId),
  }
}

/**
 * Params for updating a member's profile.
 */
export interface UpdateProfileParams {
  givenName?: string
  familyName?: string
  email?: string
  phone?: string
  memberMetadata?: MemberMetadataDTO
}
